<script lang="ts" setup>
import { computed } from "vue";
import { useResumeStore } from "~/stores/useResumeStore";
import { useUiStore } from "~/stores/useUiStore";
import type { Portfolio } from "~/typings/portfolio";
import HomeHeader from "./HomeHeader.vue";
import HomeAbout from "./HomeAbout/index.vue";
import HomeProjects from "./HomeProjects/index.vue";
import HomeSkills from "./HomeSkills/index.vue";
import HomeLanguages from "./HomeLanguages/index.vue";
import Footer from "~/components/ui/Footer/Footer.vue";

// Load data from resume store
const resumeStore = useResumeStore();
await resumeStore.loadResumeData();

// Get version from UI store
const uiStore = useUiStore();
const version = computed(() => uiStore.packageVersion);

function filterNonPersonalProjects(experiences: Portfolio.Experience[]) {
  // Filter out the portfolio project itself (clientUrl = "https://diegomarmol.com")
  // Rationale: This project will be ongoing indefinitely (always evolving with new features),
  // and showcasing it on the home page would be redundant since the visitor is already viewing it.
  // Instead, it remains visible in the full projects list for those interested in technical details.
  return experiences.filter(
    (exp) => exp.clientUrl !== "https://diegomarmol.com"
  );
}

const personalInformation = computed(() => resumeStore.personalInformation);
const socialLinks = computed(() => resumeStore.socialLinks);
const experiences = computed(() =>
  filterNonPersonalProjects(resumeStore.experiences)
);
const languages = computed(() => resumeStore.languages);
const skills = computed(() => resumeStore.skills);
const presentation = computed(() => resumeStore.presentation);
const subtitle = computed(() => personalInformation.value?.title || "");
</script>

<template>
  <template v-if="personalInformation?.name">
    <div class="relative lg:min-h-[calc(100vh-var(--main-nav-height))]">
      <HomeHeader
        :name="personalInformation.name"
        :subtitle="subtitle"
        :email="personalInformation.email"
        :image-src="personalInformation.avatar.main"
        :presentation="presentation"
      >
        <template #social>
          <div class="flex flex-col gap-2">
            <UiSocialLinks :links="socialLinks" />
          </div>
        </template>
      </HomeHeader>
    </div>

    <div
      class="lg:hidden content-width flex flex-col items-center pt-12 lg:py-12 mb-8 gap-3"
    >
      <UiRecruiterCta />
      <UiDownloadResume />
    </div>

    <div class="lg:grid lg:grid-cols-12 px-4 lg:px-0 relative overflow-x-clip">
      <div class="col-span-11 col-start-2 col-end-12 lg:my-12 lg:mt-24 mb-12">
        <!-- About -->
        <section
          id="about-me"
          class="mb-16 lg:mb-20 pt-[calc(var(--main-nav-height)+2rem)] lg:py-0"
        >
          <HomeAbout />
        </section>

        <!-- Recent Projects - Full Width -->
        <section id="experience-short" class="mb-16 lg:mb-20">
          <HomeProjects :experiences="experiences" />
        </section>

        <!-- Tools I work with - Three Columns -->
        <section id="tools" class="mb-16 lg:mb-20">
          <HomeSkills :skills="skills" />
        </section>

        <!-- Languages -->
        <section id="languages">
          <HomeLanguages :languages="languages" />
        </section>
      </div>
    </div>

    <Footer
      v-if="personalInformation.location"
      :location="personalInformation.location"
      :email="personalInformation.email"
      :version="version"
    />
  </template>
</template>
